import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Crown, Download, Package } from "lucide-react";
import { useMemo, useState } from "react";
import { SetupCard } from "@/components/setups/setup-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { usePackageDetail } from "@/hooks/use-packages";
import { useProfile } from "@/hooks/use-profile";
import { useSubscription } from "@/hooks/use-subscription";
import { analytics } from "@/lib/analytics";
import { canDownloadPackage, getTierLabel } from "@/lib/feature-access";
import type { SetupListItem } from "@/lib/types";
import { generatePackageZip } from "@/lib/zip";

export const Route = createFileRoute("/_auth/app/packages/$packageId")({
  component: PackageDetailPage,
});

function PackageDetailPage() {
  const { packageId } = Route.useParams();
  const { data: pkg, isLoading } = usePackageDetail(packageId);
  const [downloading, setDownloading] = useState<"setups" | "everything" | null>(null);

  // Normalize Supabase response — single FK relations may come as objects or arrays
  const normalizedPkg = useMemo(() => {
    if (!pkg) return null;
    return {
      ...pkg,
      season: Array.isArray(pkg.season) ? pkg.season[0] : pkg.season,
      series: Array.isArray(pkg.series) ? pkg.series[0] : pkg.series,
      setups: Array.isArray(pkg.setups) ? pkg.setups : [],
    };
  }, [pkg]);

  const { data: profile } = useProfile();
  const { data: subscription } = useSubscription(profile?.id);
  const userTier = subscription?.tier ?? "free";
  const selectedSeriesIds = subscription?.selectedSeriesIds ?? [];

  const seriesId = normalizedPkg?.series?.id ?? null;

  const isFreePackage = normalizedPkg?.is_free ?? false;

  const setupsAccess = canDownloadPackage({
    tier: userTier,
    seriesId,
    selectedSeriesIds,
    includeReplays: false,
    isFree: isFreePackage,
  });

  const everythingAccess = canDownloadPackage({
    tier: userTier,
    seriesId,
    selectedSeriesIds,
    includeReplays: true,
    isFree: isFreePackage,
  });

  async function handleDownload(includeReplays: boolean) {
    if (!normalizedPkg) return;
    const type = includeReplays ? "everything" : "setups";
    setDownloading(type);

    try {
      const result = await generatePackageZip({
        data: { packageId: normalizedPkg.id, includeReplays },
      });

      // Download via signed URL (bypasses Vercel 4.5MB body limit)
      const a = document.createElement("a");
      a.href = result.downloadUrl;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      analytics.packageDownloaded({
        packageId: normalizedPkg.id,
        packageName: normalizedPkg.name,
        includeReplays,
      });
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(null);
    }
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-24 animate-pulse rounded bg-muted" />
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
          <div className="flex h-32 animate-pulse items-center justify-center bg-muted" />
          <div className="space-y-3 p-6">
            <div className="h-7 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-5 w-1/2 animate-pulse rounded bg-muted" />
            <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  // Not found
  if (!normalizedPkg) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="font-[Bebas_Neue] text-2xl text-foreground">Package Not Found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This package may have been removed or doesn&apos;t exist.
        </p>
        <Button variant="outline" size="sm" className="mt-6" asChild>
          <Link to="/app/packages" search={{} as any}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Packages
          </Link>
        </Button>
      </div>
    );
  }

  const seasonLabel = normalizedPkg.season
    ? `${normalizedPkg.season.year} Season ${normalizedPkg.season.quarter}`
    : "";

  return (
    <div className="space-y-6">
      {/* Back link — uses browser history to preserve filters */}
      <button
        type="button"
        onClick={() => window.history.back()}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Packages
      </button>

      {/* Header card */}
      <div className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm transition-all hover:border-border hover:shadow-md">
        {/* Top accent area */}
        <div className="relative flex aspect-[5/2] items-center justify-center bg-gradient-to-br from-[#1a2744] to-[#0d1b2a]">
          {normalizedPkg.image_url ? (
            <img
              src={normalizedPkg.image_url}
              alt={normalizedPkg.name}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <Package className="h-14 w-14 text-white/20" />
          )}

          {/* Download count */}
          <div className="absolute top-4 right-4 flex items-center gap-1 rounded-md bg-black/60 px-2.5 py-1 text-xs text-white">
            <Download className="h-3 w-3" />
            {normalizedPkg.download_count}
          </div>
        </div>

        {/* Package info */}
        <div className="p-6">
          <h1 className="font-[Bebas_Neue] text-3xl leading-tight text-foreground sm:text-4xl">
            {normalizedPkg.name}
          </h1>

          {normalizedPkg.description && (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {normalizedPkg.description}
            </p>
          )}

          {/* Metadata */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {normalizedPkg.series && (
              <Badge variant="outline" className="border-border/50 text-xs text-foreground">
                {normalizedPkg.series.name}
              </Badge>
            )}

            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              {seasonLabel} &middot; Week {normalizedPkg.week_number}
            </span>
          </div>
        </div>
      </div>

      {/* Download buttons */}
      <div className="flex flex-wrap gap-3">
        {/* Download .STO Files Only */}
        {setupsAccess.allowed ? (
          <Button onClick={() => handleDownload(false)} disabled={downloading !== null}>
            <Download className="mr-2 h-4 w-4" />
            {downloading === "setups" ? "Preparing ZIP..." : "Download .STO Files Only"}
          </Button>
        ) : (
          <div className="flex flex-col gap-1">
            <Button disabled variant="default">
              <Download className="mr-2 h-4 w-4" />
              Download .STO Files Only
            </Button>
            <span className="text-xs text-muted-foreground">
              Requires{" "}
              <span className="font-medium text-[#E8872A]">
                {getTierLabel(setupsAccess.requiredTier ?? "plus")}
              </span>{" "}
              subscription
            </span>
          </div>
        )}

        {/* Download Everything */}
        {everythingAccess.allowed ? (
          <Button
            variant="outline"
            onClick={() => handleDownload(true)}
            disabled={downloading !== null}
          >
            <Download className="mr-2 h-4 w-4" />
            {downloading === "everything" ? "Preparing ZIP..." : "Download Everything"}
          </Button>
        ) : (
          <div className="flex flex-col gap-1">
            <Button disabled variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Download Everything
              <Badge className="ml-2 bg-[#E8872A] border-transparent text-white text-[10px] px-1.5 py-0">
                <Crown className="mr-0.5 h-2.5 w-2.5" />
                Premium
              </Badge>
            </Button>
            <span className="text-xs text-muted-foreground">
              Requires{" "}
              <span className="font-medium text-[#E8872A]">
                {getTierLabel(everythingAccess.requiredTier ?? "premium")}
              </span>{" "}
              subscription
            </span>
          </div>
        )}
      </div>

      <Separator className="bg-border/50" />

      {/* File list */}
      {normalizedPkg.setups.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-[Bebas_Neue] text-xl text-foreground">Package Contents</h2>
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden transition-all hover:border-border hover:shadow-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-xs text-muted-foreground">
                  <th className="px-4 py-2 text-left font-medium">Car</th>
                  <th className="px-4 py-2 text-left font-medium">Track</th>
                  <th className="px-4 py-2 text-left font-medium">Files</th>
                  <th className="px-4 py-2 text-right font-medium">Lap Time</th>
                </tr>
              </thead>
              <tbody>
                {normalizedPkg.setups.map((setup: SetupListItem) => {
                  const car = Array.isArray(setup.car) ? setup.car[0] : setup.car;
                  const track = Array.isArray(setup.track) ? setup.track[0] : setup.track;
                  const trackLabel = track?.config
                    ? `${track.name} (${track.config})`
                    : (track?.name ?? "");
                  const fileTypes = setup.file_types ?? [];
                  const lapTime = setup.latest_version?.best_lap_time;

                  const formatLap = (seconds: number) => {
                    const mins = Math.floor(seconds / 60);
                    const secs = seconds % 60;
                    const whole = Math.floor(secs);
                    const ms = Math.round((secs - whole) * 1000);
                    return `${mins}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
                  };

                  const typeLabels: Record<string, string> = {
                    race: "Race",
                    qualifying: "Quali",
                    safe: "Safe",
                    endurance: "Endurance",
                    wet: "Wet",
                    race_safe: "Race Safe",
                    qualifying_safe: "Quali Safe",
                    endurance_safe: "Endurance Safe",
                  };

                  return (
                    <tr
                      key={setup.id}
                      className="border-b border-border/30 last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-2 text-foreground">{car?.name ?? "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{trackLabel}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {fileTypes.length > 0
                          ? fileTypes.map((ft: string) => typeLabels[ft] ?? ft).join(", ")
                          : "Setup"}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                        {lapTime != null ? formatLap(Number(lapTime)) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Separator className="bg-border/50" />

      {/* Included setups (cards) */}
      <div className="space-y-4">
        <h2 className="font-[Bebas_Neue] text-xl text-foreground">Included Setups</h2>

        {normalizedPkg.setups.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {normalizedPkg.setups.map((setup: SetupListItem) => (
              <SetupCard key={setup.id} setup={setup} />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No setups in this package yet.
          </p>
        )}
      </div>
    </div>
  );
}
